import { useEffect, useState } from "react";
import { formatEther } from "viem";

import { useRpcRequest } from "../../hooks/useRpcRequest";

import type { Address } from "viem";

interface ContractMeta {
  balance: string | null;
  codeSize: number | null;
  isEOA: boolean;
  loading: boolean;
}

export function useContractMeta(address: Address | null): ContractMeta {
  const { request } = useRpcRequest();
  const [balance, setBalance] = useState<string | null>(null);
  const [codeSize, setCodeSize] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!address || address === "0x") {
      setBalance(null);
      setCodeSize(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all([
      request("eth_getBalance", [address, "latest"]),
      request("eth_getCode", [address, "latest"]),
    ])
      .then(([balHex, code]) => {
        if (cancelled) return;
        const balHexStr = String(balHex);
        const codeStr = String(code);
        setBalance(formatEther(BigInt(balHexStr)));
        setCodeSize(codeStr ? (codeStr.length - 2) / 2 : 0);
      })
      .catch(() => {
        if (cancelled) return;
        setBalance(null);
        setCodeSize(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [address, request]);

  return {
    balance,
    codeSize,
    isEOA: codeSize !== null && codeSize === 0,
    loading,
  };
}
